/**
 * Le notifiche push: registrare il dispositivo e reagire al tocco.
 *
 * ## Cosa c'era e cosa mancava
 *
 * Il backend le manda già: `INotificationService` invia via FCM, e la riga `UserDevice` ha i campi
 * `pushToken`, `platform`, `deviceName` — scrivibili via JSON:API e visibili solo al proprietario
 * (`UserDeviceResourceDefinition`). Mancava l'unico pezzo che sta sul telefono: **dire al server
 * qual è il token di questo dispositivo**. Senza, la campanella resta l'unica strada, e la
 * campanella interroga il server ogni trenta secondi **solo mentre l'app è aperta**: ad app chiusa
 * un'approvazione di ferie non raggiunge nessuno.
 *
 * ## Come si aggancia il token al dispositivo giusto
 *
 * L'app ha già un'identità del dispositivo: il *fingerprint* che manda al login e che il BE salva
 * come `deviceHash`. Il token si scrive sulla riga con quel `deviceHash` — senza inventare un
 * secondo registro, e senza toccare il flusso di login.
 *
 * Se la riga non c'è ancora (primo avvio, o login fatto da un altro dispositivo) non si crea niente:
 * la crea il BE al login successivo, e il token si registrerà allora. Meglio nessuna notifica che
 * una riga orfana in un registro di dispositivi.
 *
 * ## Il permesso si chiede quando ha un senso
 *
 * `initPush` va chiamata **dopo l'accesso**, non all'avvio: il dialogo di sistema si può mostrare
 * una volta sola, e mostrarlo sulla schermata di login — prima che l'utente abbia visto a cosa
 * serve — è il modo più efficace di farselo negare per sempre.
 */
import { updateOne } from '@/shared/api/jsonapi/client';
import { fetchMyDevices } from '@/features/users/devices';
import { platform, nativeRuntime } from '@/shared/lib/nativePlatform';

export type PushRecordingOutcome =
  | { ok: true; token: string }
  | {
    ok: false;
    /** `non-configurata`: pacchetto senza il plugin delle notifiche — vedi `pushPluginInstalled()`. */
    cause: 'piattaforma' | 'non-configurata' | 'permesso' | 'dispositivo' | 'errore';
    message: string;
  };

/** Evita di registrarsi due volte quando l'app rimonta l'albero (StrictMode, cambio tenant). */
let recordingInProgress: Promise<PushRecordingOutcome> | null = null;

/**
 * Il plugin delle notifiche è dentro questo pacchetto?
 *
 * È l'unica domanda che si fa prima di registrare il dispositivo: se `cordova-plugin-firebasex`
 * non è installato, `window.FirebasePlugin` non esiste e il codice delle push non parte. Si legge
 * dopo `deviceready`, che `main.tsx` aspetta prima di disegnare l'app.
 *
 * ⚠️ Il plugin senza la configurazione è la combinazione che rompe. `FirebaseMessaging.getInstance()`
 * pretende un `FirebaseApp` nato da `google-services.json`, e senza quel file — misurato il
 * 26 ago 2026 sull'APK v1.17.432 — l'app si chiudeva a ogni avvio dopo il consenso. Qui il file sta
 * in `APP/` ed è versionato, quindi un pacchetto col plugin ha anche la configurazione: chi
 * toglie il file deve togliere anche il plugin, non il contrario.
 */
export function pushPluginInstalled(): boolean {
  return firebasex() !== null;
}

/**
 * Scrive il token sulla riga `user-devices` di **questo** dispositivo.
 *
 * Esportata a parte perché è la metà testabile: la parte nativa è un listener, questa è una PATCH
 * con una regola di corrispondenza che, sbagliata, scriverebbe il token sul dispositivo di un'altra
 * sessione dello stesso utente — e le notifiche arriverebbero al telefono sbagliato.
 */
export async function saveTokenOnDevice(input: {
  userId: string;
  fingerprint: string;
  token: string;
  deviceName?: string | null;
}): Promise<PushRecordingOutcome> {
  const rows = await fetchMyDevices(input.userId);
  const my = rows.find((d) => d.deviceHash === input.fingerprint);

  if (!my) {
    return {
      ok: false,
      cause: 'dispositivo',
      message: 'Questo dispositivo non risulta ancora registrato: le notifiche si attiveranno al prossimo accesso.',
    };
  }

  // Già a posto: si evita una PATCH per ogni avvio dell'app.
  if (my.pushToken === input.token) return { ok: true, token: input.token };

  await updateOne('user-devices', {
    id: my.id,
    attributes: {
      pushToken: input.token,
      platform: platform(),
      ...(input.deviceName ? { deviceName: input.deviceName } : {}),
    },
  });

  return { ok: true, token: input.token };
}

export interface PushOptions {
  userId: string;
  fingerprint: string;
  /** Nome leggibile del dispositivo, se lo si conosce. Compare nella pagina «Dispositivi». */
  deviceName?: string | null;
  /**
   * Cosa fare quando l'utente tocca una notifica. Riceve il `data` del messaggio: il BE ci mette
   * ciò che serve a riaprire la cosa notificata. Senza questo aggancio la notifica apre l'app sulla
   * home, e l'utente deve ritrovare da solo quello che gli era stato annunciato.
   */
  onOpen?: (data: Record<string, unknown>) => void;
}

/**
 * Chiede il permesso, registra il dispositivo, aggancia il tocco. Da chiamare **dopo** l'accesso.
 *
 * Non solleva mai: le notifiche sono un di più, e un errore qui non deve impedire di usare l'app.
 */
export function initPush(o: PushOptions): Promise<PushRecordingOutcome> {
  /* ⚠️ `runtimeNativo() === 'web'` e non `!isNative()`, e questa riga e' la correzione di un
     difetto **mio**: il ramo Cordova era stato aggiunto piu' in basso, ma questa uscita stava
     prima e in un APK Cordova `isNative()` e' falso. Quindi il ramo nuovo era **codice morto** e
     le notifiche restavano rifiutate col messaggio di prima. E' la stessa forma dei difetti che
     questa serie sta chiudendo — una guardia che risponde alla domanda sbagliata — progetto
     mentre li chiudevo. */
  if (nativeRuntime() === 'web') {
    return Promise.resolve({
      ok: false,
      cause: 'piattaforma',
      message: 'Le notifiche push arrivano sull’app installata: dal browser restano gli avvisi nella campanella.',
    });
  }
  /* Il controllo sta **prima** della richiesta di permesso, non dopo: chiedere di attivare le
     notifiche a chi poi non le riceverà è una domanda a vuoto. */
  const fp = firebasex();
  if (!fp) {
    return Promise.resolve({
      ok: false,
      cause: 'non-configurata',
      message: 'Questa versione dell’app non ha il servizio di notifiche: gli avvisi restano nella campanella.',
    });
  }

  if (recordingInProgress) return recordingInProgress;

  /* ⚠️ Un ramo solo, e la guardia sopra e' cio' che lo rende raggiungibile. Qui sotto c'era il
     ramo del guscio precedente, con il suo giro di permessi, la registrazione e i due
     ascoltatori. Non e' andato perduto niente di funzionante: nell'APK non veniva mai eseguito,
     ed e' il difetto che il commento in testa a questa funzione racconta. */
  recordingInProgress = recordWithFirebasex(o, fp).finally(() => {
    recordingInProgress = null;
  });
  return recordingInProgress;
}

/* ───────────────────────────── il ramo Cordova ───────────────────────────── */

/**
 * `cordova-plugin-firebasex` non porta i tipi: si dichiara il minimo che si usa, con le firme
 * lette dal suo sorgente in `APP/plugins/cordova-plugin-firebasex/` di Lares — che e' la
 * stessa versione **18.0.5** pinnata in questo guscio, e per la ragione scritta nel `LEGGIMI`
 * (dalla 20 il pacchetto si divide e le notifiche restano fuori dall'installazione).
 */
interface FirebasexCordova {
  hasPermission(cb: (concesso: boolean) => void): void;
  grantPermission(cb: (concesso: boolean) => void): void;
  getToken(ok: (token: string) => void, ko: (e: unknown) => void): void;
  onMessageReceived(cb: (dati: Record<string, unknown>) => void, ko?: (e: unknown) => void): void;
}

function firebasex(): FirebasexCordova | null {
  if (typeof window === 'undefined') return null;
  return (window as { FirebasePlugin?: FirebasexCordova }).FirebasePlugin ?? null;
}

/** Il permesso, che su Android 13+ e su iOS va chiesto e puo' essere negato. */
function firebasexPermission(fp: FirebasexCordova): Promise<boolean> {
  return new Promise((resolve) => {
    fp.hasPermission((concesso) => {
      if (concesso) {
        resolve(true);
        return;
      }
      fp.grantPermission((after) => resolve(Boolean(after)));
    });
  });
}

/**
 * La registrazione col plugin Cordova.
 *
 * ⚠️ Il tocco su una notifica **non ha una richiamata sua** in firebasex: arriva dentro
 * `onMessageReceived` con il campo `tap` valorizzato (`'background'` o `'foreground'`, letto da
 * `OnNotificationReceiverActivity.java` e `FirebasePlugin.java`). Senza distinguere quel campo,
 * `onApri` scatterebbe anche per una notifica **solo ricevuta** — cioe' l'app salterebbe da
 * sola su una schermata mentre l'utente sta facendo un'altra cosa.
 *
 * ⚠️ Quel che questo ramo **non** fa, e non lo faceva nemmeno il precedente: agganciare
 * `onTokenRefresh`. Un token FCM puo' cambiare, e quando cambia le notifiche smettono di
 * arrivare **in silenzio** — il dispositivo resta registrato col vecchio. E' una lacuna dei
 * due gusci, non di questo, e va chiusa in entrambi: qui si dichiara per non farla scoprire
 * dall'assenza di notifiche.
 */
function recordWithFirebasex(o: PushOptions, fp: FirebasexCordova): Promise<PushRecordingOutcome> {
  return new Promise((risolvi) => {
    /* La scadenza c'e' per la stessa ragione per cui l'aveva il ramo precedente: una promessa mai risolta
       terrebbe in piedi il lock di registrazione per sempre. */
    const deadline = setTimeout(() => {
      risolvi({
        ok: false,
        cause: 'errore',
        message: 'Il servizio di notifiche non ha risposto: riprova più tardi.',
      });
    }, 20_000);

    void firebasexPermission(fp).then((concesso) => {
      if (!concesso) {
        clearTimeout(deadline);
        risolvi({
          ok: false,
          cause: 'permesso',
          message:
            'Le notifiche sono disattivate per questa app. Puoi riattivarle da Impostazioni → MaeFwk10 → Notifiche.',
        });
        return;
      }

      fp.getToken(
        (token) => {
          clearTimeout(deadline);
          void saveTokenOnDevice({
            userId: o.userId,
            fingerprint: o.fingerprint,
            token,
            deviceName: o.deviceName ?? null,
          })
            .then(risolvi)
            .catch(() => risolvi({
              ok: false,
              cause: 'errore',
              message: 'Non è stato possibile registrare questo dispositivo per le notifiche.',
            }));
        },
        () => {
          clearTimeout(deadline);
          risolvi({
            ok: false,
            cause: 'errore',
            message: 'Il dispositivo non è stato accettato dal servizio di notifiche. Riprova più tardi.',
          });
        },
      );

      if (o.onOpen) {
        fp.onMessageReceived((dati) => {
          /* Solo il tocco, non la ricezione: vedi il commento in testa a questa funzione. */
          if (dati && typeof dati.tap === 'string') o.onOpen?.(dati);
        });
      }
    });
  });
}
